import { apiClient } from '@/shared/services/client'
import type {
  GroupExtension,
  GroupExtensionListItem,
  PaginatedGroupExtensions,
  GroupCreateInput,
  GroupExtensionCreateInput,
  GroupExtensionUpdateInput,
  GroupExtensionsFilters,
} from '@/modules/users/types/group'

/**
 * API functions for group extension management
 * Based on backend/routers/group_extensions.py endpoints
 */

// ============================================================================
// Group Extensions CRUD
// ============================================================================

/**
 * Get paginated list of group extensions with filters
 */
export async function getGroupExtensions(
  filters?: GroupExtensionsFilters
): Promise<PaginatedGroupExtensions> {
  const response = await apiClient.get<PaginatedGroupExtensions>('/group-extensions/', {
    params: filters,
  })
  return response.data
}

/**
 * Get all group extensions without pagination
 */
export async function getAllGroupExtensions(): Promise<GroupExtensionListItem[]> {
  const response = await apiClient.get<GroupExtensionListItem[]>('/group-extensions/all/')
  return response.data
}

/**
 * Get single group extension by ID
 */
export async function getGroupExtension(extensionId: number): Promise<GroupExtension> {
  const response = await apiClient.get<GroupExtension>(`/group-extensions/${extensionId}/`)
  return response.data
}

/**
 * Create new Django Group with its GroupExtension
 */
export async function createGroup(data: GroupCreateInput): Promise<GroupExtension> {
  const response = await apiClient.post<GroupExtension>('/groups/', data)
  return response.data
}

/**
 * Create new group extension
 */
export async function createGroupExtension(
  data: GroupExtensionCreateInput
): Promise<GroupExtension> {
  const response = await apiClient.post<GroupExtension>('/group-extensions/', data)
  return response.data
}

/**
 * Update group extension (PATCH)
 */
export async function updateGroupExtension(
  extensionId: number,
  data: GroupExtensionUpdateInput
): Promise<GroupExtension> {
  const response = await apiClient.patch<GroupExtension>(`/group-extensions/${extensionId}/`, data)
  return response.data
}

/**
 * Delete group extension
 */
export async function deleteGroupExtension(
  extensionId: number
): Promise<{ message: string; success: boolean }> {
  const response = await apiClient.delete(`/group-extensions/${extensionId}/`)
  return response.data
}

/**
 * Get group extension by group ID
 */
export async function getGroupExtensionByGroup(groupId: number): Promise<GroupExtension> {
  const response = await apiClient.get<GroupExtension>(
    `/group-extensions/by-group/${groupId}/`
  )
  return response.data
}

/**
 * Get only laboratorio group extensions
 */
export async function getLaboratorios(): Promise<GroupExtensionListItem[]> {
  const response = await apiClient.get<GroupExtensionListItem[]>(
    '/group-extensions/laboratorios/'
  )
  return response.data
}

/**
 * Get only cliente (active laboratorio) group extensions
 */
export async function getClientes(): Promise<GroupExtensionListItem[]> {
  const response = await apiClient.get<GroupExtensionListItem[]>(
    '/group-extensions/clientes/'
  )
  return response.data
}

/**
 * Toggle cliente status
 */
export async function toggleCliente(extensionId: number): Promise<GroupExtension> {
  const response = await apiClient.post<GroupExtension>(
    `/group-extensions/${extensionId}/toggle-cliente/`
  )
  return response.data
}

/**
 * Toggle laboratorio status
 */
export async function toggleLaboratorio(extensionId: number): Promise<GroupExtension> {
  const response = await apiClient.post<GroupExtension>(
    `/group-extensions/${extensionId}/toggle-laboratorio/`
  )
  return response.data
}

/**
 * Toggle permiso status
 */
export async function togglePermiso(extensionId: number): Promise<GroupExtension> {
  const response = await apiClient.post<GroupExtension>(
    `/group-extensions/${extensionId}/toggle-permiso/`
  )
  return response.data
}
